/**
 * svgSolides — outillage SVG partagé du dossier espace (5e, thème #13).
 *
 * TOUT PAR CONSTRUCTION. Deux briques :
 *   1. Perspective cavalière (projection oblique) d'un pavé droit / cube, d'un
 *      prisme droit à base triangulaire et d'un cylindre de révolution. La
 *      projection est une vraie transformation affine : fuyantes PARALLÈLES à
 *      l'angle `alpha`, réduites du coefficient `k` (0,5 par défaut). On peut
 *      dégrader volontairement la figure (fuyantes divergentes, arêtes cachées
 *      en trait plein, réduction absente) pour fabriquer des distracteurs.
 *   2. Patrons (nets) du pavé, du prisme triangulaire et du cylindre, chacun
 *      avec des variantes correctes ET fausses paramétrées (disques mal placés,
 *      rayons ≠, bases non superposables) pour les QCM d'images d'O5.
 *
 * Réutilise creerFigure de svgAngles (segment/point nommé/brut/render) et figEl
 * de svgTriangles. Les figures sont mises à l'échelle uniformément (fit) : les
 * égalités de longueurs et le parallélisme sont donc conservés.
 */

import { creerFigure, ptAt } from '../angles/svgAngles';
import { figEl } from '../triangles/svgTriangles';

export { figEl };

const NOIR = '#1f2937';
/** Fond tres clair des faces d'un patron : les faces se lisent comme des faces. */
const FOND_FACE = '#eff6ff';
const GRIS = '#94a3b8';
const BLEUCLAIR = '#bfdbfe';
const ROUGECLAIR = '#fecaca';
const JAUNECLAIR = '#fef3c7';

const R = (v) => Number(v).toFixed(1);

// ═══════════════════════════════════════════════════════════
// Projection cavalière
// ═══════════════════════════════════════════════════════════

/**
 * Projette un point 3D (x droite, y HAUT, z profondeur) en coord. écran (y bas).
 * Fuyante vers le haut-droite : profondeur +z → +cos(alpha), −sin(alpha) à
 * l'écran, mise à l'échelle par le coefficient `k`.
 * `alphaZ` permet un angle de fuite PAR POINT (distracteur « fuyantes divergentes »).
 */
const projeter = (Pt, k, alphaRad) => ({
    x: Pt.x + Pt.z * k * Math.cos(alphaRad),
    y: -Pt.y - Pt.z * k * Math.sin(alphaRad),
});

/**
 * Ajuste une liste de points écran au viewBox (marge pad), échelle UNIFORME.
 * Retourne { map, k } : `map(P)` transforme un point, `k` est le facteur (pour
 * mettre à l'échelle des rayons d'ellipses).
 */
const fitEcran = (pts, vb, pad = 26) => {
    const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const dx = maxX - minX || 1, dy = maxY - minY || 1;
    const k = Math.min((vb.w - 2 * pad) / dx, (vb.h - 2 * pad) / dy);
    const offX = (vb.w - k * dx) / 2, offY = (vb.h - k * dy) / 2;
    return {
        k,
        map: (P) => ({ x: offX + (P.x - minX) * k, y: offY + (P.y - minY) * k }),
    };
};

// ═══════════════════════════════════════════════════════════
// Primitives de tracé (dans une figure creerFigure)
// ═══════════════════════════════════════════════════════════

const arete = (fig, P, Q, { dash = false, couleur = NOIR, ep = 1.7 } = {}) => {
    const da = dash ? ' stroke-dasharray="5 4"' : '';
    fig.brut(`<line x1="${R(P.x)}" y1="${R(P.y)}" x2="${R(Q.x)}" y2="${R(Q.y)}" stroke="${couleur}" stroke-width="${ep}" stroke-linecap="round"${da}/>`);
};

const polygone = (fig, pts, { fill = 'none', couleur = NOIR, ep = 1.7, dash = false } = {}) => {
    const da = dash ? ' stroke-dasharray="5 4"' : '';
    const d = pts.map((p) => `${R(p.x)},${R(p.y)}`).join(' ');
    fig.brut(`<polygon points="${d}" fill="${fill}" stroke="${couleur}" stroke-width="${ep}"${da} stroke-linejoin="round"/>`);
};

/** Ellipse (base d'un cylindre en cavalière). rx, ry déjà à l'échelle. */
const ellipse = (fig, C, rx, ry, { dash = false, couleur = NOIR, ep = 1.7, demi = null } = {}) => {
    const da = dash ? ' stroke-dasharray="5 4"' : '';
    if (demi === null) {
        fig.brut(`<ellipse cx="${R(C.x)}" cy="${R(C.y)}" rx="${R(rx)}" ry="${R(ry)}" fill="none" stroke="${couleur}" stroke-width="${ep}"${da}/>`);
        return;
    }
    // demi-ellipse : 'avant' = moitié BASSE à l'écran (vers l'observateur), 'arriere' = moitié haute.
    // De A (gauche) à B (droite), sweep 0 passe par le bas, sweep 1 par le haut.
    // (Corrigé 2026-09-09, signalement prof sur le 1117 : les deux moitiés étaient inversées.)
    const y1 = demi === 'avant' ? 0 : 180;
    const large = 0, sweep = demi === 'avant' ? 0 : 1;
    const A = { x: C.x - rx, y: C.y }, B = { x: C.x + rx, y: C.y };
    fig.brut(`<path d="M ${R(A.x)},${R(A.y)} A ${R(rx)},${R(ry)} 0 ${large} ${sweep} ${R(B.x)},${R(B.y)}" fill="none" stroke="${couleur}" stroke-width="${ep}"${da}/>`);
    void y1;
};

const disque = (fig, C, r, { couleur = NOIR, fill = 'none', ep = 1.6 } = {}) =>
    fig.brut(`<circle cx="${R(C.x)}" cy="${R(C.y)}" r="${R(r)}" fill="${fill}" stroke="${couleur}" stroke-width="${ep}"/>`);

const rect = (fig, x, y, w, h, { couleur = NOIR, fill = 'none', ep = 1.6 } = {}) =>
    fig.brut(`<rect x="${R(x)}" y="${R(y)}" width="${R(w)}" height="${R(h)}" fill="${fill}" stroke="${couleur}" stroke-width="${ep}"/>`);

const etiquette = (fig, P, txt, { dx = 0, dy = 0, fs = 13 } = {}) =>
    fig.brut(`<text x="${R(P.x + dx)}" y="${R(P.y + dy)}" font-family="serif" font-size="${fs}" font-weight="600" text-anchor="middle" fill="#0f172a">${txt}</text>`);

/** Distance d'un point au segment [S, U]. */
const distSegment = (P, S, U) => {
    const vx = U.x - S.x, vy = U.y - S.y;
    const L2 = vx * vx + vy * vy;
    const t = L2 ? Math.max(0, Math.min(1, ((P.x - S.x) * vx + (P.y - S.y) * vy) / L2)) : 0;
    return Math.hypot(P.x - (S.x + t * vx), P.y - (S.y + t * vy));
};

/**
 * La mesure d'une arête, posée à l'écart du segment [PQ].
 *
 * `eviter` : les arêtes déjà tracées. Quand on les fournit, la mesure essaie les deux
 * côtés du segment et trois inclinaisons, et garde la position qui l'éloigne le plus de
 * TOUS les traits. Sans cette liste, elle garde l'ancien comportement, un décalage
 * perpendiculaire fixe — qui posait le « 2 cm » de la profondeur EN PLEIN SUR l'arête
 * voisine (retour prof 2026-09-26).
 *
 * La distance se mesure aux deux bouts du texte et non à son seul centre : « 10 cm »
 * dépasse d'une quinzaine de pixels de part et d'autre.
 */
/** Degagement minimal entre un trait du cylindre et le bord d'un libelle de cote. */
const ECART_COTE_CYL = 10;

const cote = (fig, P, Q, txt, { off = 12, fs = 12, eviter = null } = {}) => {
    const mx = (P.x + Q.x) / 2, my = (P.y + Q.y) / 2;
    const base = Math.atan2(Q.y - P.y, Q.x - P.x) + Math.PI / 2;
    let T = { x: mx + off * Math.cos(base), y: my + off * Math.sin(base) };
    if (eviter && eviter.length) {
        // Le texte est centre : ecarter l'arete de `off` ne suffit pas, c'est son BORD
        // qui vient toucher le trait voisin. On essaie donc aussi des ecartements
        // elargis de la demi-largeur du libelle (mesure : 1,1 px avant, 2026-09-26).
        const demi = (String(txt).length * fs * 0.55) / 2;
        const m0 = Math.abs(off) || 1;
        let best = null;
        [m0, m0 + demi * 0.6, m0 + demi].forEach((m) => [1, -1].forEach((sens) => [0, -0.4, 0.4].forEach((biais) => {
            const th = base + biais;
            const d = m * sens * Math.sign(off || 1);
            const C = { x: mx + d * Math.cos(th), y: my + d * Math.sin(th) };
            const sondes = [C, { x: C.x - demi, y: C.y }, { x: C.x + demi, y: C.y }];
            const score = Math.min(...sondes.flatMap((S) => eviter.map(([A, B]) => distSegment(S, A, B))));
            if (!best || score > best.score + 0.01) best = { score, C };
        })));
        T = best.C;
    }
    fig.brut(`<text x="${R(T.x)}" y="${R(T.y + 4)}" font-family="sans-serif" font-size="${fs}" text-anchor="middle" fill="#b45309">${txt}</text>`);
};

// ═══════════════════════════════════════════════════════════
// Pavé droit / cube en perspective cavalière
// ═══════════════════════════════════════════════════════════

/**
 * Sommets ABCDEFGH d'un pavé : base ABCD (bas), face haute EFGH (E au-dessus de
 * A, F de B, G de C, H de D). Dims L (largeur, x), H (hauteur, y), P (prof., z).
 * Fuyante haut-droite → l'arête CACHÉE est le sommet arrière-bas-gauche D :
 * les 3 arêtes qui y aboutissent (DA, DC, DH) sont en pointillés.
 */
export const paveNomme = (dims, opts = {}) => {
    const { L = 4, H = 3, P = 2.5 } = dims;
    const {
        vb = { w: 260, h: 210 },
        noms = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'],
        k = 0.5, alphaDeg = 30, salt = 0,
        cachees = 'pointilles',        // 'pointilles' | 'pleines'
        fuyantes = 'paralleles',       // 'paralleles' | 'divergentes'
        reduction = true,              // false → k = 1 (pas de réduction)
        labels = true,
        coter = null,                  // { L:'…', H:'…', P:'…' } mesures à afficher
    } = opts;
    const kk = reduction ? k : 1;
    const aRad = (alphaDeg * Math.PI) / 180;
    // 3D
    const V3 = {
        A: { x: 0, y: 0, z: 0 }, B: { x: L, y: 0, z: 0 }, C: { x: L, y: 0, z: P }, D: { x: 0, y: 0, z: P },
        E: { x: 0, y: H, z: 0 }, F: { x: L, y: H, z: 0 }, G: { x: L, y: H, z: P }, H: { x: 0, y: H, z: P },
    };
    // fuyantes divergentes : angle par sommet arrière (casse le parallélisme)
    const angleDe = (nom) => {
        if (fuyantes !== 'divergentes') return aRad;
        const spread = { C: 8, D: -10, G: 10, H: -8 };
        return aRad + ((spread[nom] || 0) * Math.PI) / 180;
    };
    const ecr = {};
    Object.keys(V3).forEach((n) => { ecr[n] = projeter(V3[n], kk, angleDe(n)); });
    /**
     * La marge doit loger les COTES, qui sont posées EN DEHORS du solide : une cote
     * s'écarte de 16 px de l'arête et son texte, centré là, fait une trentaine de
     * pixels. Les 26 px de marge par défaut ne suffisaient pas, et le « 2 cm » de
     * gauche sortait du cadre (retour prof 2026-09-26).
     *
     * La marge se calcule sur le libellé le plus long réellement demandé, pour ne pas
     * rétrécir le dessin plus qu'il ne faut.
     */
    const textes = coter ? Object.values(coter).filter(Boolean).map(String) : [];
    const demiCotes = textes.length ? (Math.max(...textes.map((t) => t.length)) * 12 * 0.55) / 2 : 0;
    // cote() peut elargir son ecartement jusqu'a 16 + demi : la marge doit loger cela
    // PLUS la demi-largeur du texte, sinon le libelle sort du cadre.
    const padCotes = textes.length ? Math.ceil(Math.max(22 + demiCotes, 16 + 2 * demiCotes + 5)) : 26;
    const { map } = fitEcran(Object.values(ecr), vb, Math.max(26, padCotes));
    const p = {};
    Object.keys(ecr).forEach((n) => { p[n] = map(ecr[n]); });

    const fig = creerFigure(vb, salt);
    const dash = cachees === 'pointilles';
    // Les douze arêtes, mémorisées : les mesures s'en écartent (voir `cote`).
    const paires = [['D', 'A'], ['D', 'C'], ['D', 'H'],
        ['A', 'B'], ['B', 'C'], ['E', 'F'], ['F', 'G'], ['G', 'H'], ['H', 'E'],
        ['A', 'E'], ['B', 'F'], ['C', 'G']];
    const segments = paires.map(([u, v]) => [p[u], p[v]]);
    // arêtes cachées (au sommet arrière D) d'abord
    arete(fig, p.D, p.A, { dash });
    arete(fig, p.D, p.C, { dash });
    arete(fig, p.D, p.H, { dash });
    // arêtes visibles
    paires.slice(3).forEach(([u, v]) => arete(fig, p[u], p[v]));

    if (coter) {
        if (coter.L) cote(fig, p.A, p.B, coter.L, { off: 14, eviter: segments });
        if (coter.H) cote(fig, p.A, p.E, coter.H, { off: -16, eviter: segments });
        if (coter.P) cote(fig, p.B, p.C, coter.P, { off: -14, eviter: segments });
    }
    if (labels) {
        const dir = { A: [-9, 12], B: [-2, 15], C: [11, 8], D: [-11, 4], E: [-9, -6], F: [-2, -8], G: [11, -4], H: [10, -8] };
        noms.forEach((nm, i) => {
            const key = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'][i];
            const [dx, dy] = dir[key];
            etiquette(fig, p[key], nm, { dx, dy });
        });
    }
    return { fig, pts: p, noms, svg: fig.render() };
};

// ═══════════════════════════════════════════════════════════
// Prisme droit à base triangulaire (couché : bases aux deux bouts)
// ═══════════════════════════════════════════════════════════

/**
 * Prisme droit couché : deux triangles identiques (avant/arrière) reliés par 3
 * arêtes. Base = triangle (b large, ht hauteur), profondeur (longueur) prof.
 * Arête cachée = l'arête arrière du bas.
 */
export const prismeTri = (opts = {}) => {
    const { b = 3.4, ht = 2.4, prof = 3.2, vb = { w: 200, h: 160 }, k = 0.5, alphaDeg = 32, salt = 0, cachees = 'pointilles' } = opts;
    const aRad = (alphaDeg * Math.PI) / 180;
    // triangle avant (z=0) : sommets bas-gauche, bas-droite, haut ; z=prof arrière
    const tri = [{ x: 0, y: 0 }, { x: b, y: 0 }, { x: b * 0.42, y: ht }];
    const V3 = {};
    tri.forEach((t, i) => { V3[`f${i}`] = { x: t.x, y: t.y, z: 0 }; V3[`b${i}`] = { x: t.x, y: t.y, z: prof }; });
    const ecr = {};
    Object.keys(V3).forEach((n) => { ecr[n] = projeter(V3[n], k, aRad); });
    const { map } = fitEcran(Object.values(ecr), vb);
    const p = {};
    Object.keys(ecr).forEach((n) => { p[n] = map(ecr[n]); });
    const fig = creerFigure(vb, salt);
    const dash = cachees === 'pointilles';
    // arêtes cachées : arrière-bas (b0→b1) + fuyante b0
    arete(fig, p.b0, p.b1, { dash });
    arete(fig, p.f0, p.b0, { dash });
    // triangle avant
    arete(fig, p.f0, p.f1); arete(fig, p.f1, p.f2); arete(fig, p.f2, p.f0);
    // triangle arrière (visible : b1,b2)
    arete(fig, p.b1, p.b2); arete(fig, p.b2, p.b0, { dash });
    // fuyantes visibles
    arete(fig, p.f1, p.b1); arete(fig, p.f2, p.b2);
    return { fig, pts: p, svg: fig.render() };
};

// ═══════════════════════════════════════════════════════════
// Polygone régulier (base d'un prisme) — figure 2D exacte
// ═══════════════════════════════════════════════════════════

/**
 * Polygone régulier à `n` côtés, dessiné à plat (base d'un prisme). Sert quand
 * on parle d'un prisme à base à n côtés SANS pouvoir dessiner le solide (n
 * quelconque) : on montre honnêtement la base, pas un prisme d'une autre forme.
 * Un côté horizontal en bas. `salt` varie l'empreinte d'unicité (data-v).
 */
export const polygoneRegulier = (n, opts = {}) => {
    const { vb = { w: 180, h: 150 }, salt = 0, legende = 'base du prisme' } = opts;
    const cx = vb.w / 2;
    const cy = vb.h / 2 - (legende ? 4 : 0);
    const r = Math.min(vb.w, vb.h) * 0.36;
    const start = -Math.PI / 2 - Math.PI / n; // un côté horizontal en bas
    const pts = [];
    for (let i = 0; i < n; i++) {
        const a = start + (i * 2 * Math.PI) / n;
        pts.push(`${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`);
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" width="${vb.w}" height="${vb.h}" font-family="Arial" data-v="${salt}">`
        + `<polygon points="${pts.join(' ')}" fill="#1E3A8A" fill-opacity="0.12" stroke="#1E3A8A" stroke-width="2" stroke-linejoin="round"/>`
        + (legende ? `<text x="${cx}" y="${vb.h - 6}" text-anchor="middle" font-size="12" fill="#334155">${legende}</text>` : '')
        + `</svg>`;
};

// ═══════════════════════════════════════════════════════════
// Cylindre de révolution debout
// ═══════════════════════════════════════════════════════════

export const cylindreDebout = (opts = {}) => {
    // k et alpha sont les deux parametres libres de la cavaliere : les changer ne
    // deforme rien, la projection reste exacte. Avec k = 0,5 et alpha = 30 deg le
    // disque du haut s'ecrasait a ry = 0,25 r et le libelle du rayon se posait sur
    // l'arc (retour prof 2026-09-26). 0,6 et 45 deg ouvrent le disque a ry = 0,42 r.
    const { r = 1.6, h = 3, vb = { w: 170, h: 200 }, k = 0.6, alphaDeg = 45, salt = 0, coter = null } = opts;
    const aRad = (alphaDeg * Math.PI) / 180;
    // ellipse = projection du disque : demi-grand axe = r (horizontal), demi-petit = r*k*sin(alpha)
    const ry = r * k * Math.sin(aRad);
    // centres bas et haut (axe vertical)
    const V3 = { Cb: { x: 0, y: 0, z: 0 }, Ch: { x: 0, y: h, z: 0 }, gL: { x: -r, y: 0, z: 0 }, gR: { x: r, y: 0, z: 0 } };
    const ecr = {};
    Object.keys(V3).forEach((n) => { ecr[n] = projeter(V3[n], k, aRad); });
    // inclure les extrema d'ellipse dans le fit
    const extra = [{ x: ecr.Cb.x, y: ecr.Cb.y + ry }, { x: ecr.Ch.x, y: ecr.Ch.y - ry }];
    // Même raison que pour le pavé : la cote de hauteur est posée 16 px à DROITE du
    // cylindre et son texte fait une trentaine de pixels. La marge par défaut les
    // laissait sortir du cadre (audit de géométrie, 2026-09-25).
    const textesCyl = coter ? Object.values(coter).filter(Boolean).map(String) : [];
    // Deux libelles, deux besoins : le rayon se pose AU-DESSUS du disque (12 px + la
    // demi-hauteur du texte) et la hauteur se pose A DROITE de la generatrice, ou il
    // faut degager toute la largeur du texte — sinon « 8 cm » vient toucher le trait
    // (mesure : 1,1 px avant correction, retour prof 2026-09-26).
    const demiCyl = textesCyl.length ? (Math.max(...textesCyl.map((t) => t.length)) * 12 * 0.55) / 2 : 0;
    const padCyl = textesCyl.length ? Math.ceil(Math.max(24 + demiCyl, ECART_COTE_CYL + 2 * demiCyl + 5)) : 26;
    const { map, k: fk } = fitEcran([...Object.values(ecr), ...extra], vb, Math.max(26, padCyl));
    const p = {};
    Object.keys(ecr).forEach((n) => { p[n] = map(ecr[n]); });
    const rx = r * fk, ryS = ry * fk;
    const fig = creerFigure(vb, salt);
    // génératrices
    arete(fig, { x: p.Cb.x - rx, y: p.Cb.y }, { x: p.Ch.x - rx, y: p.Ch.y });
    arete(fig, { x: p.Cb.x + rx, y: p.Cb.y }, { x: p.Ch.x + rx, y: p.Ch.y });
    // ellipse du bas : avant plein, arrière pointillé
    ellipse(fig, p.Cb, rx, ryS, { demi: 'avant' });
    ellipse(fig, p.Cb, rx, ryS, { demi: 'arriere', dash: true });
    // ellipse du haut (pleine)
    ellipse(fig, p.Ch, rx, ryS);
    if (coter) {
        if (coter.r) { // rayon du disque du haut
            arete(fig, p.Ch, { x: p.Ch.x + rx, y: p.Ch.y }, { couleur: '#b45309', ep: 1.4 });
            // au-dessus de TOUT le disque : a 10 px le texte tombait sur l'arc
            cote(fig, p.Ch, { x: p.Ch.x + rx, y: p.Ch.y }, coter.r, { off: -(ryS + 12) });
        }
        // texte centre a droite de la generatrice : l'ecart doit loger sa DEMI-largeur
        if (coter.h) {
            const demiH = (String(coter.h).length * 12 * 0.55) / 2;
            cote(fig, { x: p.Cb.x + rx, y: p.Cb.y }, { x: p.Ch.x + rx, y: p.Ch.y }, coter.h, { off: ECART_COTE_CYL + demiH });
        }
    }
    return { fig, pts: p, svg: fig.render() };
};

// ═══════════════════════════════════════════════════════════
// PATRONS (nets)
// ═══════════════════════════════════════════════════════════

/**
 * Patron d'un cylindre : un rectangle + deux disques.
 * variante : 'correct'    → 2 disques même rayon, côtés opposés (haut & bas)
 *            'memecote'   → 2 disques du même côté (faux)
 *            'rayonsineg' → 2 disques de rayons différents (faux — le « d » du doc)
 *            'bouts'      → 2 disques aux extrémités gauche/droite (faux)
 */
/**
 * Rendu d'un patron de vignette QCM.
 *
 * Les patrons etaient poses a des coordonnees figees dans un cadre fixe. Deux
 * consequences mesurees (retour prof 2026-09-26, « on ne voit pas tres bien les
 * finitions des patrons ») : la base haute du prisme « bases du meme cote »
 * sortait de 8 px et le patron du pave de 5 px — ils etaient COUPES ; et le
 * dessin n'occupait que 21 % a 52 % de sa vignette, donc s'affichait petit.
 *
 * On decrit donc les formes dans un repere libre, puis :
 *   - le cadre est l'union des boites de TOUTES les variantes de la famille, pour
 *     que les options d'un meme QCM s'affichent exactement a la meme echelle
 *     (sinon la taille du dessin devient un indice) ;
 *   - la variante demandee y est centree ;
 *   - le cadre colle au contenu, a `marge` pres : plus de vide, donc un dessin
 *     plus grand a largeur de carte egale.
 */
const boiteDesFormes = (formes) => {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    const pt = (x, y) => { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); };
    formes.forEach((f) => {
        if (f.t === 'r') { pt(f.x, f.y); pt(f.x + f.w, f.y + f.h); }
        else if (f.t === 'c') { pt(f.x - f.r, f.y - f.r); pt(f.x + f.r, f.y + f.r); }
        else f.pts.forEach((q) => pt(q.x, q.y));
    });
    return { x0, y0, x1, y1, w: x1 - x0, h: y1 - y0 };
};

const rendrePatron = (formesDe, variantes, variante, opts = {}) => {
    const { salt = 0, marge = 12, fill = FOND_FACE, ep = 1.9 } = opts;
    // cadre commun a la famille : meme echelle pour toutes les options du QCM
    const union = boiteDesFormes(variantes.flatMap((v) => formesDe(v)));
    const vb = opts.vb || { w: Math.ceil(union.w + 2 * marge), h: Math.ceil(union.h + 2 * marge) };
    const mes = formesDe(variante);
    const b = boiteDesFormes(mes);
    const dx = (vb.w - b.w) / 2 - b.x0, dy = (vb.h - b.h) / 2 - b.y0;
    const fig = creerFigure(vb, salt);
    mes.forEach((f) => {
        if (f.t === 'r') rect(fig, f.x + dx, f.y + dy, f.w, f.h, { fill, ep });
        else if (f.t === 'c') disque(fig, { x: f.x + dx, y: f.y + dy }, f.r, { fill, ep });
        else polygone(fig, f.pts.map((q) => ({ x: q.x + dx, y: q.y + dy })), { fill, ep });
    });
    return { fig, svg: fig.render() };
};

const VARIANTES_CYL = ['correct', 'bouts', 'memecote', 'rayonsineg'];
const VARIANTES_PRISME = ['correct', 'basesmemecote', 'sansbase', 'quatrefaces'];

export const patronCylindre = (variante = 'correct', opts = {}) => {
    const rw = 120, rh = 40;               // rectangle : identique dans TOUTES les variantes
    const rx = rectDisqueRayon();          // rayon « nominal »
    const formes = (v) => {
        const f = [{ t: 'r', x: 0, y: 0, w: rw, h: rh }];
        if (v === 'bouts') {               // disques accolés aux petits côtés (faux)
            f.push({ t: 'c', x: -rh / 2, y: rh / 2, r: rh / 2 });
            f.push({ t: 'c', x: rw + rh / 2, y: rh / 2, r: rh / 2 });
        } else if (v === 'memecote') {     // les 2 disques du même côté (faux)
            f.push({ t: 'c', x: rw / 2 - rw / 4, y: -rx, r: rx });
            f.push({ t: 'c', x: rw / 2 + rw / 4, y: -rx, r: rx });
        } else if (v === 'rayonsineg') {   // rayons différents (faux)
            f.push({ t: 'c', x: rw / 2, y: -rx, r: rx });
            f.push({ t: 'c', x: rw / 2, y: rh + rx * 0.62, r: rx * 0.62 });
        } else {                           // correct : même rayon, côtés opposés, tangents
            f.push({ t: 'c', x: rw / 2, y: -rx, r: rx });
            f.push({ t: 'c', x: rw / 2, y: rh + rx, r: rx });
        }
        return f;
    };
    return rendrePatron(formes, VARIANTES_CYL, variante, opts);
};
const rectDisqueRayon = () => 26;

/**
 * Patron d'un prisme droit à base triangulaire : 3 rectangles alignés (les 3
 * faces latérales) + 2 triangles (les bases), l'un en haut du 2e rectangle,
 * l'autre en bas.
 * variante : 'correct' | 'basesmemecote' (2 triangles du même côté, faux)
 *            'sansbase' (une seule base, faux) | 'quatrefaces' (4 rectangles →
 *            base à 4 côtés, ce n'est pas un prisme TRIANGULAIRE, faux)
 */
export const patronPrismeTri = (variante = 'correct', opts = {}) => {
    const w1 = 44, w2 = 52, w3 = 44, h = 46;   // 3 rectangles (largeurs = côtés du triangle)
    const formes = (v) => {
        const largeurs = v === 'quatrefaces' ? [w1, w2, w3, 40] : [w1, w2, w3];
        const f = [];
        let xc = 0;
        largeurs.forEach((w) => { f.push({ t: 'r', x: xc, y: 0, w, h }); xc += w; });
        // triangle-base au-dessus du rectangle du milieu
        const mx = w1, mw = w2;
        f.push({ t: 'p', pts: [{ x: mx, y: 0 }, { x: mx + mw, y: 0 }, { x: mx + mw * 0.42, y: -40 }] });
        if (v === 'basesmemecote') {
            f.push({ t: 'p', pts: [{ x: mx, y: -40 }, { x: mx + mw, y: -40 }, { x: mx + mw * 0.42, y: -80 }] });
        } else if (v !== 'sansbase') {     // correct : 2e base sous le rectangle du milieu
            f.push({ t: 'p', pts: [{ x: mx, y: h }, { x: mx + mw, y: h }, { x: mx + mw * 0.42, y: h + 40 }] });
        }
        return f;
    };
    return rendrePatron(formes, VARIANTES_PRISME, variante, opts);
};

/** Patron « en croix » d'un pavé (6 rectangles). Pour l'association solide↔patron. */
export const patronPave = (opts = {}) => {
    const { a = 40, b = 54, c = 34 } = opts;   // a=largeur, b=hauteur (face avant), c=profondeur
    // bande verticale : haut(c) - avant(b) - bas(c) - arrière(b) ; ailes gauche/droite (c)
    const formes = () => [
        { t: 'r', x: 0, y: -c, w: a, h: c },
        { t: 'r', x: 0, y: 0, w: a, h: b },
        { t: 'r', x: 0, y: b, w: a, h: c },
        { t: 'r', x: 0, y: b + c, w: a, h: b },
        { t: 'r', x: -c, y: 0, w: c, h: b },
        { t: 'r', x: a, y: 0, w: c, h: b },
    ];
    return rendrePatron(formes, ['correct'], 'correct', opts);
};

// ═══════════════════════════════════════════════════════════
// Disque (aire) — cercle + rayon coté
// ═══════════════════════════════════════════════════════════

/** Disque coloré avec le rayon tracé et coté (rLabel, ex. "4 cm"). */
export const svgDisque = (opts = {}) => {
    const { vb = { w: 150, h: 150 }, salt = 0, rLabel = null, couleur = BLEUCLAIR } = opts;
    const fig = creerFigure(vb, salt);
    const O = { x: vb.w / 2, y: vb.h / 2 };
    const rr = Math.min(vb.w, vb.h) * 0.4;
    disque(fig, O, rr, { fill: couleur, ep: 1.8 });
    const E = ptAt(O, -32, rr);
    fig.brut(`<line x1="${R(O.x)}" y1="${R(O.y)}" x2="${R(E.x)}" y2="${R(E.y)}" stroke="${NOIR}" stroke-width="1.4"/>`);
    fig.brut(`<path d="M ${R(O.x - 4)},${R(O.y)} L ${R(O.x + 4)},${R(O.y)} M ${R(O.x)},${R(O.y - 4)} L ${R(O.x)},${R(O.y + 4)}" stroke="${NOIR}" stroke-width="1.2"/>`);
    if (rLabel) {
        const M = ptAt(O, -32, rr * 0.55);
        fig.brut(`<text x="${R(M.x)}" y="${R(M.y - 6)}" font-family="sans-serif" font-size="12" text-anchor="middle" fill="#b45309">${rLabel}</text>`);
    }
    return { fig, svg: fig.render() };
};

// ═══════════════════════════════════════════════════════════
// « Preuve sans les mots » — disque découpé → parallélogramme
// ═══════════════════════════════════════════════════════════

/**
 * Schéma : un disque partagé en n secteurs (haut) et les secteurs réassemblés
 * en « parallélogramme » (bas) de base ≈ π×r et de hauteur ≈ r.
 * Purement ILLUSTRATIF (la carte porte la formule en QCM/ordonner).
 */
export const svgSecteursDisque = (opts = {}) => {
    const { vb = { w: 300, h: 170 }, salt = 0, n = 8 } = opts;
    const fig = creerFigure(vb, salt);
    // Disque du haut à gauche
    const O = { x: 66, y: 60 }, rr = 46;
    for (let i = 0; i < n; i++) {
        const a1 = (360 * i) / n, a2 = (360 * (i + 1)) / n;
        const p1 = ptAt(O, a1, rr), p2 = ptAt(O, a2, rr);
        const fill = i % 2 === 0 ? ROUGECLAIR : BLEUCLAIR;
        fig.brut(`<path d="M ${R(O.x)},${R(O.y)} L ${R(p1.x)},${R(p1.y)} A ${R(rr)},${R(rr)} 0 0 1 ${R(p2.x)},${R(p2.y)} Z" fill="${fill}" stroke="${NOIR}" stroke-width="1"/>`);
    }
    // Parallélogramme réassemblé à droite : n triangles alternés
    const x0 = 150, yBase = 108, hh = 46, wSeg = 130 / (n / 2);
    for (let i = 0; i < n; i++) {
        const up = i % 2 === 0;
        const xa = x0 + Math.floor(i / 2) * wSeg + (up ? 0 : wSeg / 2);
        const fill = up ? ROUGECLAIR : BLEUCLAIR;
        const base = up ? yBase : yBase - hh;
        const apexY = up ? yBase - hh : yBase;
        polygone(fig, [{ x: xa, y: base }, { x: xa + wSeg, y: base }, { x: xa + wSeg / 2, y: apexY }], { fill, ep: 1 });
    }
    // cotes : base ≈ π×r, hauteur ≈ r
    fig.brut(`<line x1="${R(x0)}" y1="${R(yBase + 12)}" x2="${R(x0 + 130 + wSeg / 2)}" y2="${R(yBase + 12)}" stroke="${NOIR}" stroke-width="1"/>`);
    fig.brut(`<text x="${R(x0 + 70)}" y="${R(yBase + 26)}" font-family="sans-serif" font-size="12" text-anchor="middle" fill="#b45309">π × r</text>`);
    fig.brut(`<line x1="${R(x0 - 10)}" y1="${R(yBase - hh)}" x2="${R(x0 - 10)}" y2="${R(yBase)}" stroke="${NOIR}" stroke-width="1"/>`);
    fig.brut(`<text x="${R(x0 - 20)}" y="${R(yBase - hh / 2 + 4)}" font-family="sans-serif" font-size="12" text-anchor="middle" fill="#b45309">r</text>`);
    return { fig, svg: fig.render() };
};

// ═══════════════════════════════════════════════════════════
// Aire composée — stade (rect + 2 demi-disques) / carré + demi-disque
// ═══════════════════════════════════════════════════════════

/**
 * type 'stade' : rectangle horizontal + un demi-disque à chaque bout (rayon =
 *   demi-hauteur). Cotes : longueur du rectangle Llabel, hauteur totale hLabel.
 * type 'demiCarre' : carré surmonté d'un demi-disque (rayon = demi-côté).
 *   Cote : côté aLabel.
 */
export const svgAireComposee = (type, opts = {}) => {
    const { vb = { w: 250, h: 170 }, salt = 0, Llabel = '', hLabel = '', aLabel = '' } = opts;
    const fig = creerFigure(vb, salt);
    if (type === 'stade') {
        const rr = 40, Lp = 120;
        const cy = vb.h / 2, x0 = (vb.w - Lp) / 2;
        // rectangle (côtés haut/bas), demi-disques gauche/droite
        fig.brut(`<line x1="${R(x0)}" y1="${R(cy - rr)}" x2="${R(x0 + Lp)}" y2="${R(cy - rr)}" stroke="${NOIR}" stroke-width="1.8"/>`);
        fig.brut(`<line x1="${R(x0)}" y1="${R(cy + rr)}" x2="${R(x0 + Lp)}" y2="${R(cy + rr)}" stroke="${NOIR}" stroke-width="1.8"/>`);
        fig.brut(`<path d="M ${R(x0)},${R(cy - rr)} A ${R(rr)},${R(rr)} 0 0 0 ${R(x0)},${R(cy + rr)}" fill="none" stroke="${NOIR}" stroke-width="1.8"/>`);
        fig.brut(`<path d="M ${R(x0 + Lp)},${R(cy - rr)} A ${R(rr)},${R(rr)} 0 0 1 ${R(x0 + Lp)},${R(cy + rr)}" fill="none" stroke="${NOIR}" stroke-width="1.8"/>`);
        fig.brut(`<rect x="${R(x0)}" y="${R(cy - rr)}" width="${R(Lp)}" height="${R(2 * rr)}" fill="${JAUNECLAIR}" opacity="0.5" stroke="none"/>`);
        cote(fig, { x: x0, y: cy + rr }, { x: x0 + Lp, y: cy + rr }, Llabel, { off: 16 });
        fig.brut(`<text x="${R(x0 + Lp + 26)}" y="${R(cy + 4)}" font-family="sans-serif" font-size="12" text-anchor="middle" fill="#b45309">${hLabel}</text>`);
    } else { // demiCarre
        const a = 96, x0 = (vb.w - a) / 2, y0 = vb.h - 26 - a;
        rect(fig, x0, y0, a, a, { fill: BLEUCLAIR });
        fig.brut(`<path d="M ${R(x0)},${R(y0)} A ${R(a / 2)},${R(a / 2)} 0 0 1 ${R(x0 + a)},${R(y0)} Z" fill="${BLEUCLAIR}" stroke="${NOIR}" stroke-width="1.8"/>`);
        cote(fig, { x: x0, y: y0 + a }, { x: x0 + a, y: y0 + a }, aLabel, { off: 16 });
    }
    return { fig, svg: fig.render() };
};
